// src/admin/UpdateHolyName.js
import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { updateStudentHolyName } from '../FirebaseService';
import './UpdateHolyName.css'; // Import the new CSS file

const UpdateHolyName = () => {
  const { allStudentsForOutlet } = useOutletContext();
  const { refreshData } = useContext(StudentContext);
  const [currentHolyName, setCurrentHolyName] = useState('');
  const [newHolyName, setNewHolyName] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const { uniqueHolyNames, noHolyNameCount } = useMemo(() => {
    const counts = new Map();
    let noHolyNameCount = 0;
    allStudentsForOutlet.forEach((student) => {
      const name = student.holyName ? student.holyName.trim() : '';
      if (!name) {
        noHolyNameCount++;
      } else {
        counts.set(name, (counts.get(name) || 0) + 1);
      }
    });
    const unique = Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
    return { uniqueHolyNames: unique, noHolyNameCount };
  }, [allStudentsForOutlet]);

  useEffect(() => {
    if (currentHolyName === 'Chưa có tên Thánh') {
      const filtered = allStudentsForOutlet.filter((student) => !student.holyName || student.holyName.trim() === '');
      setFilteredStudents(filtered);
    } else if (currentHolyName.trim() === '') {
      setFilteredStudents([]);
    } else {
      const filtered = allStudentsForOutlet.filter((student) => student.holyName === currentHolyName);
      setFilteredStudents(filtered);
    }
  }, [currentHolyName, allStudentsForOutlet]);

  const handleFilter = (e) => {
    setCurrentHolyName(e.target.value);
  };

  const handleSelectHolyName = (holyName) => {
    setCurrentHolyName(holyName);
  };

  const handleUpdate = async () => {
    if (!newHolyName.trim()) {
      setMessage('Vui lòng nhập tên thánh mới.');
      return;
    }
    if (filteredStudents.length === 0) {
      setMessage('Không có thiếu nhi nào để cập nhật.');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      for (const student of filteredStudents) {
        await updateStudentHolyName(student.id, newHolyName);
      }
      setMessage(`Đã cập nhật thành công cho ${filteredStudents.length} thiếu nhi.`);
      refreshData();
      setFilteredStudents([]);
    } catch (error) {
      console.error('Error updating holy names:', error);
      setMessage('Lỗi khi cập nhật. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update-holy-name-wrapper">
      <aside className="update-holy-name-sidebar">
        <h3 className="update-holy-name-sidebar-title">Các loại tên thánh</h3>
        <ul className="update-holy-name-sidebar-list">
          <li
            key="no-holy-name"
            className="update-holy-name-sidebar-item"
            onClick={() => handleSelectHolyName('Chưa có tên Thánh')}
          >
            <span className="update-holy-name-sidebar-name">Chưa có tên Thánh</span>
            <span className="update-holy-name-sidebar-count"> ({noHolyNameCount})</span>
          </li>
          {uniqueHolyNames.map(({ name, count }) => (
            <li
              key={name}
              className="update-holy-name-sidebar-item"
              onClick={() => handleSelectHolyName(name)}
            >
              <span className="update-holy-name-sidebar-name">{name}</span>
              <span className="update-holy-name-sidebar-count"> ({count})</span>
            </li>
          ))}
        </ul>
      </aside>
      <div className="update-holy-name-main">
        <h2 className="update-holy-name-title">Cập nhật Tên Thánh Thiếu Nhi</h2>
        <div className="update-holy-name-input-group">
          <label className="update-holy-name-label">Tên thánh hiện tại (để tìm kiếm):</label>
          <input
            type="text"
            value={currentHolyName}
            onChange={handleFilter}
            className="update-holy-name-input"
            placeholder="Nhập tên thánh hiện tại (ví dụ: Anna)"
          />
        </div>
        <div className="update-holy-name-input-group">
          <label className="update-holy-name-label">Tên thánh mới:</label>
          <input
            type="text"
            value={newHolyName}
            onChange={(e) => setNewHolyName(e.target.value)}
            className="update-holy-name-input"
            placeholder="Nhập tên thánh mới (ví dụ: An Na)"
          />
        </div>
        <button
          onClick={handleUpdate}
          disabled={loading || filteredStudents.length === 0}
          className="update-holy-name-button"
        >
          {loading ? 'Đang cập nhật...' : 'Xác nhận cập nhật'}
        </button>
        {message && <p className="update-holy-name-message">{message}</p>}

        {filteredStudents.length > 0 && (
          <div className="update-holy-name-student-list">
            <h3 className="update-holy-name-student-list-title">Danh sách thiếu nhi sẽ được cập nhật ({filteredStudents.length}):</h3>
            <ul className="update-holy-name-student-items">
              {filteredStudents.map((student) => (
                <li key={student.id} className="update-holy-name-student-item">
                  <span className="update-holy-name-student-name">{student.fullName}</span> (ID: {student.id}, Lớp: {student.className}, Tên thánh hiện tại: {student.holyName || 'Chưa có'})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateHolyName;